import type { Building } from "building-types/building/buildingTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ConfigurableTable } from "lib/house/dataSequences/dataSequenceConfigurableTable";
import {
  aggregatorColumnNew,
  categoryColumnNew,
  configurableTableNew,
  SortBy,
  stringColumnNew,
} from "lib/house/dataSequences/dataSequenceConfigurableTable";
import type { RoomByFloorMaterialEntry } from "procure/roomSelectors";
import { selectRoomsByFloorMaterial } from "procure/roomSelectors";

export interface RoomKBMData {
  idMatHash: string;
  roomHandle: DocEntityHandle;
  name: string;
  area: number;
  level: string;
  floorMaterial: string;
}

function isRoom(roomData: RoomKBMData): RoomKBMData | undefined {
  if (roomData.name) {
    return roomData;
  } else {
    return undefined;
  }
}

/** @deprecated - use the Building Query API instead */
export function getRoomsFromBuilding(building: Building): RoomKBMData[] {
  const rooms = selectRoomsByFloorMaterial(building);

  const roomKBMEntries: RoomKBMData[] = [];

  const minRoomSize = 1.5;

  for (const k in rooms) {
    const roomByFloorMaterialEntry = rooms[k] as { [key: string]: RoomByFloorMaterialEntry };
    for (const roomIdHash in roomByFloorMaterialEntry) {
      const room = roomByFloorMaterialEntry[roomIdHash] as RoomByFloorMaterialEntry;
      const area: number = Number(room[1].split(" ")[0]);
      if (area > minRoomSize) {
        roomKBMEntries.push({
          name: room[0],
          area,
          level: room[3],
          floorMaterial: room[2],
          idMatHash: roomIdHash,
          roomHandle: room[4],
        });
      }
    }
  }

  return roomKBMEntries;
}

export function makeRoomsTable(building: Building): ConfigurableTable {
  const roomKBMEntries = getRoomsFromBuilding(building);

  return configurableTableNew<RoomKBMData, RoomKBMData>({
    iterables: roomKBMEntries,
    requirement: isRoom,
    columns: [
      categoryColumnNew("Rooms"),
      stringColumnNew({
        name: "ID Mat Hash",
        fieldFunc: (counter) => counter.idMatHash,
        hidden: true,
        useInDiff: true,
      }),
      stringColumnNew({
        name: "Level",
        fieldFunc: (counter) => counter.level,
        sortBy: SortBy.Ascending,
      }),
      stringColumnNew({
        name: "Name",
        fieldFunc: (counter) => counter.name,
        sortBy: SortBy.None,
      }),
      stringColumnNew({
        name: "Floor Material",
        fieldFunc: (counter) => counter.floorMaterial,
      }),
    ],
    aggregator: aggregatorColumnNew({
      name: "Area (SF)",
      aggregatorFunc: (counter) => (counter.area ? counter.area : 0),
    }),
  });
}
